import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EntityDetailContent from '@/components/EntityDetailContent';
import MunicipalitiesField from '@/components/Location/MunicipalitiesField';
import ProvincesField from '@/components/Location/ProvincesField';
import useCheckUpdated from '@/hooks/useCheckUpdated';
import createClearRenderView from '@/utils/createClearRenderView';
import { mapRelatedFields } from '@/utils/createRelatedField';
import { renderMunicipality, renderProvince } from '@/utils/location';

import RegionField from '../../../components/Location/RegionField';

export const CU = 'Cuba';

const DynamicFieldAddressLocation = ({
  required,
  provinces,
  municipalities,
  form,
  keyPrefix,
  related,
  isCU,
  viewMode
}) => {
  const { t } = useTranslation('actors');
  const [updatedKey, setUpdatedKey] = useState(`${keyPrefix}-${related}`);
  
  const rules = useMemo(() => (required ? [{ whitespace: true }] : []), [required]);
  
  const _isCU = useMemo(() => isCU || related === CU, [related, isCU]);
  
  useCheckUpdated(() => {
    const values = form.getFieldsValue(true);
    set(values, `${keyPrefix}.province`, '');
    set(values, `${keyPrefix}.municipality`, '');
    form.setFieldsValue(cloneDeep(values));
    setUpdatedKey(`${keyPrefix}-${related}`);
  }, related);
  
  const fields = useMemo(
    () =>
      mapRelatedFields([
        ...(_isCU
          ? [
            {
              key: `${keyPrefix}.province`,
              label: t('province'),
              required,
              widgetProps: { filterBy: provinces },
              widget: ProvincesField,
              viewWidget: ({ value }) => renderProvince(value),
              colSpan: 1,
            },
            {
              key: `${keyPrefix}.municipality`,
              label: t('municipality'),
              required,
              widgetProps: { filterBy: municipalities },
              related: `${keyPrefix}.province`,
              widget: MunicipalitiesField,
              viewWidget: ({ value }) => renderMunicipality(value),
              colSpan: 1,
            },
          ]
          : [
            {
              key: `${keyPrefix}.province`,
              label: t('state'),
              required,
              widgetProps: { related },
              widget: RegionField,
              colSpan: 1,
            },
            {
              key: `${keyPrefix}.municipality`,
              label: t('city'),
              renderView: createClearRenderView(),
              rules,
              required,
              colSpan: 1,
            },
          ]),
      ]),
    [_isCU, keyPrefix, t, required, provinces, municipalities, related, rules]
  );
  
  return (
    <EntityDetailContent
      form={form}
      fields={fields}
      key={updatedKey}
      viewMode={viewMode}
    />
  );
};

DynamicFieldAddressLocation.propTypes = {
  required: PropTypes.bool,
  isCU: PropTypes.bool,
  provinces: PropTypes.array,
  municipalities: PropTypes.array,
  keyPrefix: PropTypes.string,
  related: PropTypes.string,
  form: PropTypes.object,
  viewMode: PropTypes.bool,
};

export default DynamicFieldAddressLocation;

// eslint-disable-next-line no-unused-vars
export const renderDynamicFieldAddressLocation = ({ field, rules, form }) => {
  return <DynamicFieldAddressLocation form={form}/>;
};
